import { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Plus, Save, Trash2, Zap } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { edgeAuthHeader } from '@/lib/edgeAuth';

type Provider = 'groq' | 'anthropic' | 'openai' | 'openrouter';
interface ChainEntry { provider: Provider; model: string }

interface TestResult extends ChainEntry {
  ok: boolean;
  ms: number;
  tokens?: number | null;
  items?: number;
  sample?: string;
  reason?: string;
}

const PROVIDER_LABELS: Record<Provider, string> = {
  groq: 'Groq',
  anthropic: 'Anthropic (Claude)',
  openai: 'OpenAI',
  openrouter: 'OpenRouter',
};

// Suggestions only; any model id can be typed. Verified against each
// provider's model list on 2026-09-26.
const MODEL_SUGGESTIONS: Record<Provider, string[]> = {
  groq: ['openai/gpt-oss-120b', 'openai/gpt-oss-20b'],
  anthropic: ['claude-haiku-4-5', 'claude-sonnet-5', 'claude-opus-5'],
  openai: ['gpt-6-luna', 'gpt-6-sol'],
  openrouter: ['qwen/qwen3.8-27b:free', 'nvidia/nemotron-3-super-120b-a12b:free'],
};

function isChainEntry(v: unknown): v is ChainEntry {
  if (!v || typeof v !== 'object') return false;
  const e = v as Record<string, unknown>;
  return typeof e.model === 'string' && typeof e.provider === 'string' && e.provider in PROVIDER_LABELS;
}

interface Props {
  /** Called after a successful save so the parent can refresh its audit log. */
  onSaved: (message: string) => void;
}

/** Ordered AI provider chain (app_config.LLM_CHAIN). The first entry that
 *  answers serves; the edge function moves down the list on any failure. */
export function LlmChainEditor({ onSaved }: Props) {
  const [chain, setChain] = useState<ChainEntry[]>([]);
  const [savedChain, setSavedChain] = useState<ChainEntry[]>([]);
  const [keys, setKeys] = useState<Record<Provider, boolean> | null>(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState<TestResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const functionUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/llm-api`;

  const load = useCallback(async () => {
    if (!supabase) return;
    const { data } = await supabase.from('app_config').select('value').eq('key', 'LLM_CHAIN').maybeSingle();
    const parsed = Array.isArray(data?.value) ? (data.value as unknown[]).filter(isChainEntry) : [];
    setChain(parsed);
    setSavedChain(parsed);
    try {
      const res = await fetch(`${functionUrl}/admin/providers`, { headers: { Authorization: await edgeAuthHeader() } });
      if (res.ok) {
        const body = await res.json() as { providers: Record<Provider, boolean> };
        setKeys(body.providers);
      }
    } catch {
      setKeys(null);
    }
  }, [functionUrl]);

  useEffect(() => { load(); }, [load]);

  const dirty = JSON.stringify(chain) !== JSON.stringify(savedChain);
  const valid = chain.length > 0 && chain.every(e => e.model.trim().length > 0);

  const update = (i: number, patch: Partial<ChainEntry>) =>
    setChain(prev => prev.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  const move = (i: number, dir: -1 | 1) =>
    setChain(prev => {
      const next = [...prev];
      const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  const remove = (i: number) => setChain(prev => prev.filter((_, j) => j !== i));
  const add = () => setChain(prev => [...prev, { provider: 'groq', model: '' }]);

  const save = async () => {
    if (!supabase || !valid) return;
    setSaving(true);
    setError(null);
    const cleaned = chain.map(e => ({ provider: e.provider, model: e.model.trim() }));
    const { error: rpcError } = await supabase.rpc('update_admin_config', { p_key: 'LLM_CHAIN', p_value: cleaned });
    setSaving(false);
    if (rpcError) { setError(rpcError.message); return; }
    setSavedChain(cleaned);
    setChain(cleaned);
    onSaved('LLM_CHAIN saved. Live within 60 seconds.');
  };

  const test = async () => {
    if (!valid) return;
    setTesting(true);
    setError(null);
    setResults(null);
    try {
      const res = await fetch(`${functionUrl}/admin/test-chain`, {
        method: 'POST',
        headers: { Authorization: await edgeAuthHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ chain: chain.map(e => ({ provider: e.provider, model: e.model.trim() })) }),
      });
      const body = await res.json() as { results?: TestResult[]; error?: string };
      if (!res.ok || !body.results) { setError(body.error || `Test failed (${res.status})`); return; }
      setResults(body.results);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="p-3 rounded-xl bg-[var(--bg-elevated)] border border-[var(--border)] space-y-3">
      <div>
        <p className="text-xs font-medium text-[var(--text-primary)]">AI provider chain</p>
        <p className="text-[10px] text-[var(--text-secondary)] font-mono">LLM_CHAIN</p>
        <p className="text-[10px] text-[var(--text-secondary)] mt-1">
          Tried top to bottom on every AI request. A retired model, a rate limit or a timeout moves to the next row. Keys live in Supabase secrets, not here.
        </p>
      </div>

      <div className="space-y-2">
        {chain.map((entry, i) => {
          const keyPresent = keys ? keys[entry.provider] : null;
          return (
            <div key={i} className="flex items-center gap-2">
              <span className="w-4 text-[10px] text-[var(--text-secondary)] text-right">{i + 1}</span>
              <select
                value={entry.provider}
                onChange={e => update(i, { provider: e.target.value as Provider, model: '' })}
                className="px-2 py-1 rounded-lg border border-[var(--border)] bg-[var(--bg-primary)] text-xs text-[var(--text-primary)]"
              >
                {(Object.keys(PROVIDER_LABELS) as Provider[]).map(p => (
                  <option key={p} value={p}>{PROVIDER_LABELS[p]}</option>
                ))}
              </select>
              <input
                type="text"
                list={`llm-models-${entry.provider}`}
                value={entry.model}
                placeholder="model id"
                onChange={e => update(i, { model: e.target.value })}
                className="flex-1 min-w-0 px-2 py-1 rounded-lg border border-[var(--border)] bg-[var(--bg-primary)] text-xs font-mono text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-teal)]"
              />
              <span
                title={keyPresent === null ? 'Key status unknown' : keyPresent ? 'API key set' : 'API key missing: this row is skipped'}
                className={`w-2 h-2 rounded-full flex-shrink-0 ${keyPresent === null ? 'bg-[var(--text-secondary)]' : keyPresent ? 'bg-[var(--accent-teal)]' : 'bg-[var(--status-red)]'}`}
              />
              <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" className="p-1 rounded text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-30"><ArrowUp className="w-3.5 h-3.5" /></button>
              <button onClick={() => move(i, 1)} disabled={i === chain.length - 1} aria-label="Move down" className="p-1 rounded text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-30"><ArrowDown className="w-3.5 h-3.5" /></button>
              <button onClick={() => remove(i)} aria-label="Remove" className="p-1 rounded text-[var(--text-secondary)] hover:text-[var(--status-red)]"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          );
        })}
      </div>

      {(Object.keys(MODEL_SUGGESTIONS) as Provider[]).map(p => (
        <datalist key={p} id={`llm-models-${p}`}>
          {MODEL_SUGGESTIONS[p].map(m => <option key={m} value={m} />)}
        </datalist>
      ))}

      <div className="flex items-center gap-2">
        <button onClick={add} className="px-2 py-1 rounded-lg text-xs border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Add row
        </button>
        <button onClick={test} disabled={!valid || testing} className="px-2 py-1 rounded-lg text-xs border border-[var(--accent-gold)]/50 text-[var(--accent-gold)] hover:bg-[var(--accent-gold)]/10 disabled:opacity-30 flex items-center gap-1">
          <Zap className={`w-3.5 h-3.5 ${testing ? 'animate-pulse' : ''}`} /> {testing ? 'Testing…' : 'Test each row'}
        </button>
        <div className="flex-1" />
        <button onClick={save} disabled={!dirty || !valid || saving} className={`px-3 py-1 rounded-lg text-xs font-semibold flex items-center gap-1 ${dirty && valid ? 'bg-[var(--accent-teal)] text-white hover:bg-[var(--accent-teal)]/80' : 'bg-[var(--bg-primary)] text-[var(--text-secondary)]'} disabled:opacity-30`}>
          <Save className={`w-3.5 h-3.5 ${saving ? 'animate-spin' : ''}`} /> Save chain
        </button>
      </div>

      {error && <p className="text-xs text-[var(--status-red)]">{error}</p>}

      {results && (
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-left text-[var(--text-secondary)]">
              <th className="py-1 pr-2">Row</th><th className="pr-2">Result</th><th className="pr-2">Latency</th><th className="pr-2">Tokens</th><th>Sample hook</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r, i) => (
              <tr key={i} className="border-t border-[var(--border)] align-top">
                <td className="py-1 pr-2 font-mono whitespace-nowrap">{r.provider}/{r.model}</td>
                <td className={`pr-2 font-semibold whitespace-nowrap ${r.ok ? 'text-[var(--accent-teal)]' : 'text-[var(--status-red)]'}`}>{r.ok ? `pass (${r.items} items)` : r.reason}</td>
                <td className="pr-2 whitespace-nowrap">{r.ms ? `${(r.ms / 1000).toFixed(1)}s` : '-'}</td>
                <td className="pr-2 whitespace-nowrap">{r.tokens ?? '-'}</td>
                <td className="text-[var(--text-secondary)]">{r.sample}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
